import { useState } from 'react';
import { Key, ExternalLink, Zap } from 'lucide-react';
import { setApiKey } from '../../utils/geminiService';
import useVoiceDumpStore from '../../stores/useVoiceDumpStore';
import useAICreditStore from '../../stores/useAICreditStore';

export default function CreditPrompt({ errorType }) {
  const [key, setKey] = useState('');
  const [saved, setSaved] = useState(false);
  const openWizard = useVoiceDumpStore(s => s.openWizard);
  const selectedTemplate = useVoiceDumpStore(s => s.selectedTemplate);

  const isCreditsOut = errorType === 'INSUFFICIENT_CREDITS' || errorType === 'NO_CREDITS_OR_KEY';

  const handleSave = () => {
    if (!key.trim()) return;
    setApiKey(key.trim(), false); // session-only; Settings page for persistent storage
    setSaved(true);
    // Refresh credit store (it checks for key presence too) and reopen wizard
    useAICreditStore.getState().initCredits(useAICreditStore.getState().userId);
    setTimeout(() => openWizard(selectedTemplate), 300);
  };

  return (
    <div className="vd-record" style={{ gap: 'var(--space-md)' }}>
      <div style={{ fontSize: 'var(--font-3xl)' }}>{isCreditsOut ? '💳' : '🔑'}</div>

      <h3 style={{ margin: 0, fontSize: 'var(--font-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-primary)' }}>
        {isCreditsOut ? 'AI Credits Depleted' : 'Gemini API Key Required'}
      </h3>

      <p style={{ color: 'var(--text-secondary)', textAlign: 'center', maxWidth: '360px', fontSize: 'var(--font-sm)' }}>
        {isCreditsOut 
          ? "You've used all your AI credits for this billing period. You can enter your own Google Gemini API key to continue using AI features, or wait for your credits to reset."
          : "Voice Dump uses Google Gemini to analyze your voice. Enter your API key below. It's stored only for this browser session and never sent to our servers."}
      </p>

      <div style={{ width: '100%', maxWidth: '360px', marginTop: 'var(--space-sm)' }}>
        <div style={{ position: 'relative' }}>
          <Key size={16} style={{ position: 'absolute', left: 'var(--space-sm)', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            type="password"
            className="input-field"
            placeholder="Enter BYOK (AIza...)"
            value={key}
            onChange={e => setKey(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSave()}
            /* Clears the leading icon: input padding + icon box + the gap after it.
               36 has no --space step (32 and 40 flank it); expressed as the sum it
               is made of so it tracks the icon if that changes. */
            style={{ paddingLeft: 'calc(var(--space-sm) + var(--size-md) + var(--space-xxs))' }}
            autoFocus
          />
        </div>
      </div>

      <button
        className="btn btn-primary"
        onClick={handleSave}
        disabled={!key.trim() || saved}
        style={{ width: '100%', maxWidth: '360px', marginTop: 'var(--space-xs)' }}
      >
        {saved ? '✓ Saved & Ready' : 'Save Key & Continue'}
      </button>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)', marginTop: 'var(--space-sm)' }}>
        <a
          href="https://aistudio.google.com/app/apikey"
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: 'var(--font-xs)', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-xxs)', textDecoration: 'none' }}
        >
          Get a free API key from Google AI Studio <ExternalLink size={12} />
        </a>
      </div>
    </div>
  );
}
